import React, { useState, useEffect } from 'react';
import GeovisLogo from './GeovisLogo';
import ThemeToggle from './ThemeToggle';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useScrolled } from '../hooks/useScrolled';
import { Menu, X, ArrowRight, Globe, ChevronDown, Check } from 'lucide-react';
import { LOCALES, LOCALE_ORDER } from '../data/locales';
import { URL_SOLICITAR_ACCESO } from '../lib/urbanismoBase';

// Navegación global: los productos primero (Urbanismo es la apuesta principal).
// `activo` decide qué enlace se marca según la ruta.
const NAV_LINKS = [
  { to: '/urbanismo', label: 'Urbanismo', testid: 'urbanismo', activo: (p) => p.startsWith('/urbanismo') },
  { to: '/territorio', label: 'Territorio', testid: 'territorio', activo: (p) => p === '/territorio' || p === '/renovables' },
  { to: '/informe', label: 'Informe', testid: 'informe', activo: (p) => p === '/informe' },
  { to: '/blog', label: 'Blog', testid: 'blog', activo: (p) => p.startsWith('/blog') },
];

export const Header = ({ onOpenDemo }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const scrolled = useScrolled(8);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileLocaleOpen, setMobileLocaleOpen] = useState(false);

  const { pathname } = location;
  const isHome = pathname === '/';
  // Ninguna página lleva ya el héroe cinematográfico (#gv-hero); se conserva la lógica
  // de barra transparente por si vuelve.
  const tieneHeroCine = false;
  // En las páginas de Urbanismo el CTA es pedir acceso (no hay prueba gratis); la home,
  // que es de plataforma, lleva el contacto general.
  // En la home también: Urbanismo es la puerta principal y la CTA es la misma en toda la web.
  const contextoUrbanismo = isHome || pathname.startsWith('/urbanismo');

  const currentLocaleCode = LOCALE_ORDER.find((code) => LOCALES[code].path === location.pathname);
  const currentLocale = currentLocaleCode ? LOCALES[currentLocaleCode] : LOCALES.es;

  // Barra translúcida tipo Mercury mientras el héroe cinematográfico (#gv-hero)
  // está en pantalla; pasa a la card "glass" clara en cuanto se sale de él (o
  // directamente en cualquier página sin ese héroe).
  const [heroInView, setHeroInView] = useState(tieneHeroCine);
  useEffect(() => {
    if (!tieneHeroCine) {
      setHeroInView(false);
      return;
    }
    const hero = document.getElementById('gv-hero');
    if (!hero || !('IntersectionObserver' in window)) {
      setHeroInView(false);
      return;
    }
    setHeroInView(true);
    const io = new IntersectionObserver(
      ([entry]) => setHeroInView(entry.isIntersecting),
      { rootMargin: '-72px 0px 0px 0px' },
    );
    io.observe(hero);
    return () => io.disconnect();
  }, [tieneHeroCine]);
  const heroActive = tieneHeroCine && heroInView;

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileLocaleOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const requestDemo = onOpenDemo || (() => navigate('/demo'));

  const goHome = () => {
    if (isHome) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  const closeMobileMenus = () => {
    setMobileMenuOpen(false);
    setMobileLocaleOpen(false);
  };

  const getNavLinkClass = (isActive) =>
    heroActive
      ? `rounded-md px-4 py-2 transition-colors hover:bg-white/10 ${isActive ? 'text-white' : 'text-white/80 hover:text-white'}`
      : `relative transition-colors px-3 py-2 group hover:text-primary-text ${isActive ? 'text-foreground' : 'text-foreground/60'}`;

  const pillCtaClass = heroActive
    ? 'btn-vivo btn-vivo-shine inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-5 bg-white text-[#151312] hover:bg-white/90'
    : 'btn-vivo btn-vivo-shine inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-5 bg-primary text-primary-foreground hover:bg-primary/90';

  const loginLinkClass = heroActive
    ? 'btn-vivo inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 text-white/80 hover:text-white hover:bg-white/10'
    : 'inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 border border-foreground/25 text-foreground hover:border-foreground/40 hover:bg-accent transition-colors duration-200';

  const localeTriggerClass = heroActive
    ? 'inline-flex items-center gap-1.5 rounded-md text-sm font-medium h-10 px-3 border border-white/25 bg-white/5 text-white/80 hover:bg-white/10 hover:text-white transition-colors duration-200'
    : 'inline-flex items-center gap-1.5 rounded-md text-sm font-medium h-10 px-2.5 text-foreground/65 hover:text-foreground hover:bg-accent transition-colors duration-200';

  return (
    <header data-testid="site-header" className="fixed top-0 w-full z-50 transition-all duration-300">
      <div className="mx-auto max-w-7xl px-6">
        <div
          className={`grid grid-cols-[auto_1fr_auto] h-20 items-center px-4 sm:px-6 transition-all duration-300 ${
            heroActive
              ? 'bg-transparent border border-transparent'
              : `rounded-[calc(var(--radius)+8px)] mt-4 ${
                  scrolled
                    ? 'glass-premium shadow-lg shadow-foreground/10 border border-border'
                    : 'glass-subtle border border-border/50'
                }`
          }`}
        >
          {/* Logo */}
          <button
            data-testid="site-logo-link"
            onClick={goHome}
            className={`flex items-center gap-2.5 transition-opacity duration-200 hover:opacity-80 ${
              heroActive ? 'text-white' : 'text-[oklch(var(--foreground))]'
            }`}
            aria-label="Ir a inicio"
          >
            <GeovisLogo className="h-9 w-auto" sobreVideo={heroActive} />
          </button>

          {/* Desktop Navigation — centrada de verdad (columna central del grid) */}
          <nav
            className="hidden md:flex items-center justify-center gap-1 text-sm font-medium"
            aria-label="Navegación principal"
          >
            {NAV_LINKS.map((link) => {
              const esActivo = link.activo(pathname);
              return (
                <Link
                  key={link.testid}
                  data-testid={`site-header-${link.testid}-link`}
                  to={link.to}
                  className={getNavLinkClass(esActivo)}
                  aria-current={esActivo ? 'page' : undefined}
                >
                  {link.label}
                  {!heroActive && (
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full rounded-full" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Acciones (desktop a la derecha / botón de menú en móvil) — 3ª columna del grid */}
          <div className="flex items-center justify-end gap-3">
            {/* Preferencias (tema, país) discretas; la cuenta (iniciar sesión) y la CTA, tras un separador. */}
            <div className="hidden md:flex items-center gap-2">
              <ThemeToggle sobreHero={heroActive} sinBorde />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid="site-header-locale-trigger"
                    aria-label="Seleccionar país"
                    className={localeTriggerClass}
                  >
                    <Globe className={`w-4 h-4 ${heroActive ? 'text-white/70' : 'text-foreground/60'}`} />
                    <span className="text-xs font-semibold uppercase tracking-wide">
                      {currentLocaleCode || 'es'}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="max-h-80 w-56">
                  {LOCALE_ORDER.map((code) => {
                    const l = LOCALES[code];
                    const isActive = code === currentLocaleCode;
                    return (
                      <DropdownMenuItem key={code} asChild className="focus:bg-muted focus:text-foreground">
                        <Link
                          to={l.path}
                          data-testid={`site-header-locale-${code}`}
                          className="flex w-full items-center justify-between gap-2 cursor-pointer"
                        >
                          <span className={isActive ? 'font-semibold text-primary-text' : ''}>{l.countryName}</span>
                          {isActive && <Check className="w-3.5 h-3.5 text-primary-text" />}
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
              <span className={`mx-1 h-6 w-px ${heroActive ? 'bg-white/25' : 'bg-border'}`} aria-hidden="true" />
              <a href="https://aplicacion.geovis.es/" target="_blank" rel="noopener noreferrer" className={loginLinkClass}>
                Iniciar sesión
              </a>
              {contextoUrbanismo ? (
                <Link data-testid="site-header-acceso-button" to={URL_SOLICITAR_ACCESO} className={pillCtaClass}>
                  Solicitar acceso
                  <ArrowRight className="ml-1.5 w-4 h-4" />
                </Link>
              ) : (
                <Button data-testid="site-header-demo-button" onClick={requestDemo} className={pillCtaClass}>
                  Solicitar demo
                  <ArrowRight className="ml-1.5 w-4 h-4" />
                </Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="flex md:hidden items-center gap-2">
              <ThemeToggle sobreHero={heroActive} />
              <button
                data-testid="site-header-mobile-menu-toggle"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`p-2 rounded-lg transition-colors ${
                  heroActive ? 'text-white hover:bg-white/10' : 'text-foreground hover:bg-[oklch(var(--muted))]'
                }`}
                aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Panel */}
        <div
          id="mobile-menu"
          className={`md:hidden absolute left-6 right-6 mt-2 transition-all duration-300 ease-out ${
            mobileMenuOpen
              ? 'opacity-100 translate-y-0 pointer-events-auto'
              : 'opacity-0 -translate-y-4 pointer-events-none'
          }`}
          aria-hidden={!mobileMenuOpen}
        >
          <div className="glass-premium rounded-[calc(var(--radius)+6px)] border border-border shadow-xl overflow-hidden max-h-[calc(100vh-7rem)] overflow-y-auto">
            <nav className="p-4 space-y-1" aria-label="Navegación móvil">
              {NAV_LINKS.map((link) => {
                const esActivo = link.activo(pathname);
                return (
                  <Link
                    key={link.testid}
                    data-testid={`mobile-site-header-${link.testid}-link`}
                    to={link.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                      esActivo
                        ? 'bg-[oklch(var(--primary)/0.1)] text-[oklch(var(--primary-text))]'
                        : 'text-foreground hover:bg-[oklch(var(--muted))]'
                    }`}
                    aria-current={esActivo ? 'page' : undefined}
                  >
                    {link.label}
                  </Link>
                );
              })}

              <div className="pt-1">
                <button
                  type="button"
                  data-testid="mobile-site-header-locale-toggle"
                  aria-label="Seleccionar país"
                  aria-expanded={mobileLocaleOpen}
                  onClick={() => setMobileLocaleOpen((open) => !open)}
                  className="flex items-center justify-between w-full px-4 py-3 rounded-lg text-base font-medium text-foreground hover:bg-[oklch(var(--muted))] transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    País: {currentLocale.countryName}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${mobileLocaleOpen ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${mobileLocaleOpen ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="flex flex-wrap gap-2 px-4 pt-1 pb-3">
                    {LOCALE_ORDER.map((code) => {
                      const l = LOCALES[code];
                      const isActive = code === currentLocaleCode;
                      return (
                        <Link
                          key={code}
                          to={l.path}
                          data-testid={`mobile-site-header-locale-${code}`}
                          onClick={closeMobileMenus}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                            isActive
                              ? 'border-primary/50 bg-primary/10 text-foreground'
                              : 'border-border text-foreground/70 hover:text-primary-text hover:border-primary/40'
                          }`}
                        >
                          <span>{l.countryName}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            </nav>
            <div className="p-4 pt-2 border-t border-border space-y-2">
              <a
                href="https://aplicacion.geovis.es/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-full rounded-md text-sm font-medium h-10 px-4 py-2 border border-border bg-transparent hover:bg-[oklch(var(--muted))] transition-all duration-300"
                onClick={() => setMobileMenuOpen(false)}
              >
                Iniciar sesión
              </a>
              {contextoUrbanismo ? (
                <Link
                  data-testid="mobile-site-header-acceso-button"
                  to={URL_SOLICITAR_ACCESO}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center w-full rounded-md text-sm font-medium h-10 px-4 bg-[oklch(var(--primary))] text-primary-foreground hover:bg-[oklch(var(--primary)/0.92)] transition-all duration-300"
                >
                  Solicitar acceso
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              ) : (
                <Button
                  data-testid="mobile-site-header-demo-button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    requestDemo();
                  }}
                  className="w-full bg-[oklch(var(--primary))] text-primary-foreground hover:bg-[oklch(var(--primary)/0.92)] transition-all duration-300"
                >
                  Solicitar demo
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Overlay para cerrar el menú */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/20 backdrop-blur-sm -z-10"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}
    </header>
  );
};

export default Header;
